import * as THREE from "three";
import { Palette } from "../scene";
import { AnimRig } from "../rig";
import { HQ_YAW, cube, hqNode, hqTowerName, lib, tierFourModel } from "./common";
import { dressTierFour } from "./tier4Look";
import { TOWER_SHOT } from "./projectiles";
import { Muzzle, measureMuzzle } from "./towerMuzzle";
import { ChimneySmoke } from "./chimneySmoke";
import { BaseSkin } from "./baseSkin";
import { WornSkins } from "../skins/wornSkins";
import { PennantFacing } from "./pennantFacing";

/**
 * A built tower visual. `turret` is what the plain-block path rotates and recoils; HQ models instead expose the
 * `<asset>.weapon` node (`weapon`, with its rest transform) that the fire clip animates and the view yaws after
 * `rig.update`. `rig` is a no-op rig without an HQ model.
 */
export interface TowerModel {
  storeys: THREE.Group; turret: THREE.Object3D; restY: number; rig: AnimRig; weapon: THREE.Object3D | null; weaponRest: { position: THREE.Vector3; quaternion: THREE.Quaternion } | null; name: string;
  /** Where the shot leaves (views/towerMuzzle.ts), and the part name the VFX router asks for it by; null without one. */
  muzzle: Muzzle | null; muzzlePart: string | null;
}

/** The hitscan towers' emitters (the projectile towers' parts are `TOWER_SHOT[id].muzzle`). */
const HITSCAN_MUZZLE: Readonly<Record<string, string>> = { lightning: "crystal", beacon: "orb" };
/**
 * Towers whose muzzle marker is the mouth itself (towerMuzzle.ts measure `atMarker`): the Mortar's `bore` is a disc in
 * the mouth of a barrel pointing up, which the forward (−z) search for a barrel's front would carry 20 cm off it.
 */
export const MUZZLE_AT_MARKER: ReadonlySet<string> = new Set(["mortar"]);
/** The model part a tower's shot leaves from, or null (the fortress strikes from its conductor, see vfxRouter.ts). */
export function muzzlePartOf(archId: string, isBase: boolean): string | null {
  return isBase ? null : TOWER_SHOT[archId]?.muzzle ?? HITSCAN_MUZZLE[archId] ?? null;
}

/**
 * The v6 base is a 3 × 2 fortress whose origin sits half a cell behind the gate cell (art/DEV_HANDOFF.md §1). The sim
 * still owns one cell — the gate — so the model is offset here; the footprint reservation is a sim/level decision.
 */
export const BASE_ORIGIN_OFFSET_Z = -0.5;

/**
 * Builds the visual for a tower archetype at a tier: the HQ model for that tier when present; coloured blocks
 * when the library is not up at all; nothing (an empty group) when the library is up but this model is not in. Shared by the level view and the hub scene. The HQ model
 * wears the player's skin (FINISH_PLAN WP8): the fortress's model skin by default, and every model's material skin.
 * `skin` is the model suffix: the fortress skin's, or for a tower the worn tower kit's own model (big kits,
 * WornSkins.towerModel) by default; null draws the stock model (a preview then dresses it in the skin it shows).
 */
export function buildTowerModel(archId: string, isBase: boolean, tier: number, skin: string | null = isBase ? BaseSkin.current() : WornSkins.towerModel(archId), branch: string | null = null): TowerModel {
  const storeys = new THREE.Group();
  const L = lib();
  const plain = (turret: THREE.Object3D, restY: number): TowerModel => ({ storeys, turret, restY, rig: new AnimRig(storeys, []), weapon: null, weaponRest: null, name: "", muzzle: null, muzzlePart: null });
  if (!L) {
    const color = (Palette as Record<string, number>)[archId] ?? Palette.archer;
    storeys.add(cube(new THREE.Vector3(0, 0.25, 0), 0.7, 0.5, 0.7, color));
    const turret = cube(new THREE.Vector3(0, 0.75, 0), 0.5 + 0.12 * (tier - 1), 0.5, 0.5 + 0.12 * (tier - 1), color);
    storeys.add(turret);
    return plain(turret, 0.75);
  }
  const hq = hqTowerName(archId, isBase, tier, skin, branch);
  if (L.hq(hq)) {
    const model = L.clone(`hq:${hq}`);
    WornSkins.dressTower(model, archId, isBase);        // the worn skin's materials (skins/wornSkins.ts, WP8)
    // Tier IV without its own model yet (M21): the t3 model in gold (tier4Look.ts).
    if (!isBase && tier >= 4 && branch !== null && !hq.startsWith(tierFourModel(archId, branch))) dressTierFour(model, L.height(`hq:${hq}`));
    model.rotation.y = HQ_YAW;
    if (isBase && (L.anchors.get(hq)?.half_x ?? 0) > 1) model.position.z = BASE_ORIGIN_OFFSET_Z;
    storeys.add(model);
    // measured at rest, before the rig first poses the clone
    const muzzlePart = muzzlePartOf(archId, isBase);
    const muzzle = muzzlePart ? measureMuzzle(model, hq, muzzlePart, MUZZLE_AT_MARKER.has(archId)) : null;
    // The fortress's baked chimney puffs could not fade; they are swapped for faded smoke and their tracks dropped.
    const clips = isBase ? ChimneySmoke.install(model, L.clipsOf(`hq:${hq}`)) : L.clipsOf(`hq:${hq}`);
    const rig = new AnimRig(model, clips);
    rig.playLoop("idle", 1, true);
    // the fortress pennant faces the game camera (the level re-aims it after turning the fortress to its heading)
    if (isBase) PennantFacing.face(storeys, hq, 0, clips);
    const weapon = hqNode(model, hq, "weapon");
    const weaponRest = weapon ? { position: weapon.position.clone(), quaternion: weapon.quaternion.clone() } : null;
    // A turret group at ~55 % of the height keeps the plain-block recoil/droop hooks working for HQ models too.
    const turret = new THREE.Group();
    turret.position.y = L.height(`hq:${hq}`) * 0.55;
    storeys.add(turret);
    return { storeys, turret, restY: turret.position.y, rig, weapon, weaponRest, name: hq, muzzle, muzzlePart: muzzle ? muzzlePart : null };
  }
  // the model did not ship or failed to load: nothing is drawn (an empty turret keeps the view's hooks working)
  const turret = new THREE.Group();
  storeys.add(turret);
  return plain(turret, 0);
}

const UP = new THREE.Vector3(0, 1, 0);
const _recoil = new THREE.Vector3(), _turn = new THREE.Quaternion();
/**
 * Aims an HQ weapon node after the mixer wrote its pose: keeps the clip's recoil along the barrel and adds the yaw —
 * and, with `tilt` (in the weapon parent's frame, before the yaw: a gun's elevation, an archer's lean), that too.
 */
export function aimWeapon(m: TowerModel, yaw: number, tilt: THREE.Quaternion | null = null): void {
  if (!m.weapon || !m.weaponRest) return;
  const w = m.weapon;
  _turn.setFromAxisAngle(UP, yaw);
  if (tilt) _turn.multiply(tilt);
  const recoil = _recoil.copy(w.position).sub(m.weaponRest.position).applyQuaternion(_turn);
  w.position.copy(m.weaponRest.position).add(recoil);
  w.quaternion.premultiply(_turn);
}

/**
 * The rig overlay (rig.ts AnimRig.overlay) for an aimed weapon: `clear` takes it back to `clipPose` (the clips' pose
 * this frame, poseWeapon's) for the moment an action starts, `reapply` puts the aim back. Allocated once per model.
 */
export function weaponOverlay(w: THREE.Object3D, clipPose: { readonly position: THREE.Vector3; readonly quaternion: THREE.Quaternion }): { clear(): void; reapply(): void } {
  const aimedAt = new THREE.Vector3(), aimedQ = new THREE.Quaternion();
  return {
    clear: () => { aimedAt.copy(w.position); aimedQ.copy(w.quaternion); w.position.copy(clipPose.position); w.quaternion.copy(clipPose.quaternion); },
    reapply: () => { w.position.copy(aimedAt); w.quaternion.copy(aimedQ); },
  };
}
